import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { LanguageSwitcher } from '../components/i18n/LanguageSwitcher'
import { YoutubeDataSaverToggle } from '../components/youtube/YoutubeDataSaverToggle'
import { useI18n } from '../i18n'
import {
  applyImportReplaceAll,
  buildExportPayload,
  countLocalPersonalData,
  downloadExportJson,
  resetLocalPersonalData,
  type LocalDataCounts,
} from '../storage/backup/exportImport'
import {
  parseAndValidateImportJson,
  type ImportPreview,
} from '../storage/backup/validate'
import {
  getPreferences,
  isYoutubeDataSaverEnabled,
  savePreferences,
} from '../storage/repositories/references'
import styles from './DataManagement.module.css'

type PendingImport = {
  preview: ImportPreview
  // validated payload held until confirm — not written yet
  apply: () => Promise<void>
}

export function DataManagement() {
  const { t } = useI18n()
  const fileRef = useRef<HTMLInputElement>(null)
  const [counts, setCounts] = useState<LocalDataCounts | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(null)
  const [resetConfirmText, setResetConfirmText] = useState('')
  const [dataSaverEnabled, setDataSaverEnabled] = useState(true)
  const [prefsReady, setPrefsReady] = useState(false)

  const refreshCounts = async () => {
    try {
      setCounts(await countLocalPersonalData())
      setLoadError(false)
    } catch {
      setLoadError(true)
    }
  }

  useEffect(() => {
    void refreshCounts()
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const prefs = await getPreferences()
        if (cancelled) return
        setDataSaverEnabled(isYoutubeDataSaverEnabled(prefs))
      } catch {
        /* keep default on */
      } finally {
        if (!cancelled) setPrefsReady(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleDataSaverChange = (enabled: boolean) => {
    setDataSaverEnabled(enabled)
    void savePreferences({ deferYoutubeLoad: enabled }).catch(() => {
      setError(t('data.prefsSaveFailed'))
    })
  }

  const handleExport = async () => {
    setBusy(true)
    setError(null)
    setStatus(null)
    try {
      const payload = await buildExportPayload()
      downloadExportJson(payload)
      setStatus(
        t('data.exportStatus', {
          references: payload.references.length,
          notes: payload.notes.length,
          favourites: payload.favourites.length,
        }),
      )
    } catch {
      setError(t('data.exportFailed'))
    } finally {
      setBusy(false)
    }
  }

  const handleFileChosen = async (file: File | null) => {
    setError(null)
    setStatus(null)
    setPendingImport(null)
    if (!file) return

    setBusy(true)
    try {
      const text = await file.text()
      const result = parseAndValidateImportJson(text)
      if (!result.ok) {
        setError(result.error)
        return
      }
      setPendingImport({
        preview: result.preview,
        apply: async () => {
          await applyImportReplaceAll(result.payload)
        },
      })
    } catch {
      setError(t('data.importReadFailed'))
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleConfirmImport = async () => {
    if (!pendingImport) return
    if (!window.confirm(t('data.importConfirm'))) return

    setBusy(true)
    setError(null)
    setStatus(null)
    try {
      await pendingImport.apply()
      setPendingImport(null)
      await refreshCounts()
      setStatus(t('data.importApplied'))
    } catch {
      setError(t('data.importWriteFailed'))
    } finally {
      setBusy(false)
    }
  }

  const handleReset = async () => {
    if (resetConfirmText.trim().toUpperCase() !== 'RESET') {
      setError(t('data.resetTypeError'))
      return
    }
    if (!window.confirm(t('data.resetConfirm'))) return

    setBusy(true)
    setError(null)
    setStatus(null)
    try {
      await resetLocalPersonalData()
      setResetConfirmText('')
      setPendingImport(null)
      await refreshCounts()
      setStatus(t('data.resetDone'))
    } catch {
      setError(t('data.resetFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="app-shell">
      <div className="page-header">
        <Link to="/" className="btn btn-ghost">
          {t('common.back')}
        </Link>
        <h1>{t('data.title')}</h1>
        <LanguageSwitcher compact />
      </div>

      <p className={styles.lead}>{t('data.lead')}</p>

      {loadError && (
        <p className="banner banner-error" role="alert">
          {t('data.loadError')}
        </p>
      )}

      <section className={`surface ${styles.card}`}>
        <h2>{t('data.onDevice')}</h2>
        {counts ? (
          <ul className={styles.counts}>
            <li>{t('data.references', { count: counts.referenceCount })}</li>
            <li>{t('data.timings', { count: counts.segmentCount })}</li>
            <li>{t('data.notes', { count: counts.noteCount })}</li>
            <li>{t('data.favourites', { count: counts.favouriteCount })}</li>
            <li>{t('data.performances', { count: counts.performanceCount })}</li>
          </ul>
        ) : (
          <p className="banner banner-info" role="status">
            {t('data.loadingCounts')}
          </p>
        )}
        <p className="field-hint">{t('data.storageHint')}</p>
      </section>

      <section className={`surface ${styles.card}`}>
        <YoutubeDataSaverToggle
          enabled={dataSaverEnabled}
          onChange={handleDataSaverChange}
          disabled={!prefsReady || busy}
          variant="panel"
        />
      </section>

      <section className={`surface ${styles.card}`}>
        <h2>{t('data.export')}</h2>
        <p>{t('data.exportBody')}</p>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={() => void handleExport()}
        >
          {t('data.exportJson')}
        </button>
      </section>

      <section className={`surface ${styles.card}`}>
        <h2>{t('data.import')}</h2>
        <p>{t('data.importBody')}</p>
        <div className={styles.row}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => void handleExport()}
          >
            {t('data.exportFirst')}
          </button>
          <label className={`btn btn-primary ${styles.fileLabel}`}>
            {t('data.chooseFile')}
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className={styles.fileInput}
              disabled={busy}
              onChange={(e) => void handleFileChosen(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>

        {pendingImport && (
          <div className={styles.preview} role="region" aria-label={t('data.importPreview')}>
            <h3>{t('data.importPreview')}</h3>
            <p className="banner banner-warning" role="status">
              {t('data.importPreviewWarn')}
            </p>
            <ul className={styles.counts}>
              <li>
                {pendingImport.preview.exportedAt
                  ? t('data.schemaExported', {
                      version: pendingImport.preview.schemaVersion,
                      date: pendingImport.preview.exportedAt,
                    })
                  : t('data.schemaOnly', { version: pendingImport.preview.schemaVersion })}
              </li>
              <li>
                {t('data.previewReferences', { count: pendingImport.preview.referenceCount })}
              </li>
              <li>{t('data.previewTimings', { count: pendingImport.preview.segmentCount })}</li>
              <li>{t('data.previewNotes', { count: pendingImport.preview.noteCount })}</li>
              <li>
                {t('data.previewFavourites', { count: pendingImport.preview.favouriteCount })}
              </li>
              <li>
                {t('data.previewPerformances', {
                  count: pendingImport.preview.performanceCount,
                })}
              </li>
              <li>
                {pendingImport.preview.hasPreferences
                  ? t('data.preferencesIncluded')
                  : t('data.preferencesEmpty')}
              </li>
            </ul>
            <div className={styles.row}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() => setPendingImport(null)}
              >
                {t('data.cancelImport')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void handleConfirmImport()}
              >
                {t('data.confirmImport')}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className={`surface ${styles.card}`}>
        <h2>{t('data.reset')}</h2>
        <p>{t('data.resetBody')}</p>
        <div className="field">
          <label htmlFor="reset-confirm">{t('data.resetLabel')}</label>
          <input
            id="reset-confirm"
            value={resetConfirmText}
            onChange={(e) => setResetConfirmText(e.target.value)}
            autoComplete="off"
            disabled={busy}
          />
        </div>
        <button
          type="button"
          className="btn btn-danger"
          disabled={busy || resetConfirmText.trim().toUpperCase() !== 'RESET'}
          onClick={() => void handleReset()}
        >
          {t('data.resetButton')}
        </button>
      </section>

      {status && (
        <p className="banner banner-success" role="status">
          {status}
        </p>
      )}
      {error && (
        <p className="banner banner-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
